import { useState, useEffect, useCallback } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, CheckCircle, XCircle, CalendarDays } from 'lucide-react';
import { pb, TutorProfile } from '@/lib/pocketbase';
import { format, getDay } from 'date-fns';

interface TutorAvailabilityCalendarProps {
  tutor: TutorProfile;
}

interface TimeSlot {
  start: string;
  end: string;
}

interface DayAvailability {
  day: string;
  slots: TimeSlot[];
}

// Map day names to day-of-week numbers (0 = Sunday, 1 = Monday, etc.)
const dayNameToNumber: { [key: string]: number } = {
  'sunday': 0,
  'monday': 1,
  'tuesday': 2,
  'wednesday': 3,
  'thursday': 4,
  'friday': 5,
  'saturday': 6,
};

const TutorAvailabilityCalendar = ({ tutor }: TutorAvailabilityCalendarProps) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [bookedSlots, setBookedSlots] = useState<{ [key: string]: TimeSlot[] }>({});
  const [isLoading, setIsLoading] = useState(true);

  // Get available day numbers from tutor's availability
  const getAvailableDayNumbers = useCallback((): number[] => {
    if (!tutor?.availability?.timeSlots) return [];
    
    return tutor.availability.timeSlots
      .filter((slot: DayAvailability) => slot.slots && slot.slots.length > 0)
      .map((slot: DayAvailability) => dayNameToNumber[slot.day.toLowerCase()])
      .filter((num): num is number => num !== undefined);
  }, [tutor?.availability?.timeSlots]);

  // Check if a date is an available day
  const isAvailableDay = useCallback((date: Date): boolean => {
    const availableDayNumbers = getAvailableDayNumbers();
    const dayOfWeek = getDay(date);
    return availableDayNumbers.includes(dayOfWeek);
  }, [getAvailableDayNumbers]);

  // Check if a date has bookings
  const hasBookingsOnDate = useCallback((date: Date): boolean => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return !!bookedSlots[dateKey] && bookedSlots[dateKey].length > 0;
  }, [bookedSlots]);

  // Fetch booked slots for the tutor
  const fetchBookedSlots = useCallback(async () => {
    if (!tutor?.id) return;
    
    setIsLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      
      const bookings = await pb.collection('bookings').getFullList({
        filter: `tutorId="${tutor.id}" && date>="${today}" && status!="cancelled" && status!="rejected"`
      });

      console.log('📅 Fetched bookings for availability calendar:', bookings.length);

      const slotsByDate: { [key: string]: TimeSlot[] } = {};
      
      bookings.forEach(booking => {
        // Handle different date formats
        let dateKey = booking.date;
        if (dateKey?.includes('T')) {
          dateKey = dateKey.split('T')[0];
        } else if (dateKey?.includes(' ')) {
          dateKey = dateKey.split(' ')[0];
        }
        
        if (!slotsByDate[dateKey]) {
          slotsByDate[dateKey] = [];
        }
        slotsByDate[dateKey].push({
          start: booking.startTime,
          end: booking.endTime
        });
      });

      console.log('📅 Booked slots by date:', slotsByDate);
      setBookedSlots(slotsByDate);
    } catch (error) {
      console.error('Error fetching booked slots:', error);
    } finally {
      setIsLoading(false);
    }
  }, [tutor?.id]);

  useEffect(() => {
    fetchBookedSlots();
  }, [fetchBookedSlots]);

  // Get tutor's availability for a specific day of the week
  const getAvailabilityForDay = (date: Date): TimeSlot[] => {
    if (!tutor?.availability?.timeSlots) return [];
    
    const dayName = format(date, 'EEEE');
    const daySlot = tutor.availability.timeSlots.find(
      (slot: DayAvailability) => slot.day.toLowerCase() === dayName.toLowerCase()
    );
    
    return daySlot?.slots || [];
  };

  // Check if a slot is booked
  const isSlotBooked = (date: Date, slot: TimeSlot): boolean => {
    const dateKey = format(date, 'yyyy-MM-dd');
    const bookedForDate = bookedSlots[dateKey] || [];
    
    return bookedForDate.some(
      booked => booked.start === slot.start && booked.end === slot.end
    );
  };

  const selectedDateAvailability = selectedDate ? getAvailabilityForDay(selectedDate) : [];
  const availableDayNumbers = getAvailableDayNumbers();

  // Get available day names for display
  const availableDayNames = tutor?.availability?.timeSlots
    ?.filter((slot: DayAvailability) => slot.slots && slot.slots.length > 0)
    .map((slot: DayAvailability) => slot.day) || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Calendar */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center text-lg">
            <CalendarDays className="h-5 w-5 mr-2 text-primary" />
            Schedule Calendar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            className="rounded-md border pointer-events-auto"
            fromDate={new Date()}
            modifiers={{
              available: (date) => isAvailableDay(date) && date >= new Date(new Date().setHours(0, 0, 0, 0)),
              hasBookings: (date) => hasBookingsOnDate(date),
              unavailable: (date) => !isAvailableDay(date) && date >= new Date(new Date().setHours(0, 0, 0, 0)),
            }}
            modifiersClassNames={{
              available: 'bg-green-100 text-green-800 font-semibold hover:bg-green-200',
              hasBookings: 'ring-2 ring-orange-400 ring-inset',
              unavailable: 'text-muted-foreground/50',
            }}
            disabled={(date) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return date < today;
            }}
          />
          <div className="mt-4 space-y-2">
            <div className="flex flex-wrap gap-3 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-100 border border-green-300" />
                <span className="text-muted-foreground">Available</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-background ring-2 ring-orange-400 ring-inset" />
                <span className="text-muted-foreground">Has Bookings</span>
              </div>
            </div>
            {availableDayNames.length > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                Available on: {availableDayNames.join(', ')}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Time Slots for Selected Date */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center text-lg">
            <Clock className="h-5 w-5 mr-2 text-primary" />
            {selectedDate
              ? `${format(selectedDate, 'EEEE, MMM d, yyyy')}`
              : 'Select a date'
            }
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : selectedDateAvailability.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground mb-4">
                Available time slots for this day:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {selectedDateAvailability.map((slot, index) => {
                  const booked = selectedDate ? isSlotBooked(selectedDate, slot) : false;
                  return (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        booked
                          ? 'bg-destructive/10 border-destructive/30 text-destructive'
                          : 'bg-green-50 border-green-200 text-foreground hover:bg-green-100'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span className="font-medium text-sm">
                          {slot.start} - {slot.end}
                        </span>
                      </div>
                      {booked ? (
                        <Badge variant="destructive" className="text-xs">
                          <XCircle className="h-3 w-3 mr-1" />
                          Booked
                        </Badge>
                      ) : (
                        <Badge className="text-xs bg-green-600 hover:bg-green-700 text-white border-0">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Open
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : selectedDate ? (
            <div className="text-center py-8">
              <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground">
                No availability on {format(selectedDate, 'EEEE')}s
              </p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                Try selecting a different day
              </p>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p>Select a date to view time slots</p>
              {availableDayNames.length > 0 && (
                <p className="text-sm mt-2">
                  Available on {availableDayNames.join(' & ')}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TutorAvailabilityCalendar;
